<template>
  <div>
    <v-container>
      <div v-show="isVisibleProp"></div>
      <v-row>
        <v-col cols="3">
          <v-text-field
            v-model.number="editedItem.fiscalYear"
            id="saiMonthlyTransItemFYText"
            class="sai-monthly-trans-item-fy-text"
            label="Fiscal Year"
            readonly
          ></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="editedItem.section"
            id="saiMonthlyTransItemSAISectionText"
            class="sai-monthly-trans-item-sai-section-text"
            label="SAI Section"
            item-text="description"
            item-value="name"
            :items="saiSections"
            readonly
          ></v-select>
        </v-col>
        <v-col cols="3" v-if="activeSection === 'BANKS'">
          <v-select
            v-model="editedItem.bank"
            id="saiMonthlyTransItemBankSelect"
            class="sai-monthly-trans-item-bank-select"
            label="Bank"
            :items="banks"
            item-text="bankName"
            item-value="_id"
            autofocus
            readonly
            @focus="roundBankAmountFields"
          ></v-select>
        </v-col>
        <v-col cols="3" v-if="activeSection === 'FUNDS' || activeSection  === 'COLLECTIONS'">
          <v-select
            v-model="editedItem.fund"
            id="saiMonthlyTransItemFundSelect"
            class="sai-monthly-trans-item-fund-select"
            label="Fund"
            :items="funds"
            item-text="fundDesc"
            item-value="_id"
            autofocus
            @focus="roundFundCollectionsAmountFields"
            readonly
          ></v-select>
        </v-col>
        <v-col cols="3" v-if="activeSection === 'OFFICIALDEPOSITORY'">
          <v-select
            v-model="editedItem.odDepartment"
            id="saiMonthlyTransItemodSelect"
            class="sai-monthly-trans-item-od-select"
            label="Official Depository"
            :items="departmentList"
            item-text="caption"
            item-value="id"
            autofocus
            @focus="roundODAmountFields"
          ></v-select>
        </v-col>
      </v-row>
      <v-row v-if="activeSection === 'BANKS'">
        <BanksMonthlyTransaction
          :banksMonthlyTransaction="editedItem.banksMonthlyTransaction"
          @updateBanksMonthlyTransaction="updateBanksMonthlyTransaction"
        />
      </v-row>
      <v-row v-if="activeSection  === 'COLLECTIONS'">
        <CollectionsMonthlyTransaction
          :collectionMonthlyTransaction="editedItem.collectionMonthlyTransaction"
          @updateCollectionsMonthlyTransaction="updateCollectionsMonthlyTransaction"
        />
      </v-row>
      <v-row v-if="activeSection === 'FUNDS'">
        <FundsMonthlyTransaction
          v-model="editedItem.fundsMonthlyTransaction"
          :baseCrudKey="baseCrudKey"
        />
      </v-row>
      <v-row v-if="activeSection  === 'OFFICIALDEPOSITORY'">
        <ODMonthlyTransaction
          v-model="editedItem.odMonthlyTransaction"
          :baseCrudKey="baseCrudKey"
        />
      </v-row>
    </v-container>
  </div>
</template>

<script>
/*
  using v-model for FundsMonthlyTransaction & ODMonthlyTransaction
  using :value , @input for BanksMonthlyTransaction & CollectionsMonthlyTransaction
  will compare which approach is better and beneficial
*/
import { mapGetters, mapMutations, mapState } from 'vuex';
import { amountFormat, amountToNumber } from '../../../../util/shared/vue-global';
import BanksMonthlyTransaction from './BanksMonthlyTransaction.vue';
import CollectionsMonthlyTransaction from './CollectionsMonthlyTransaction.vue';
import FundsMonthlyTransaction from './FundsMonthlyTransaction.vue';
import ODMonthlyTransaction from './ODMonthlyTransaction.vue';

const bankCrudKey = 'sharedBank';
const fundCrudKey = 'sharedFund';
const collectionSection = ['unapportionLastMonth', 'receipts', 'transferIn', 'transferOut', 'refundsPaymentCancelled', 'total', 'apportioned', 'balanceUnapportioned', 'saiSection'];
const bankSection = ['balPerBankStatement', 'investment', 'reconcilement', 'adjBankBalance', 'glBankBalance', 'difference', 'saiSection'];
const fundSection = ['balanceLastMonth', 'apportioned', 'transferIn', 'transferOut', 'disbursements', 'od', 'balance', 'saiSection'];
const odSection = ['balanceLastMonth', 'deposits', 'transferIn', 'transferOut', 'vouchersRegistered', 'balance', 'saiSection'];

export default {
  name: 'SaiMonthlyTransEditedItem',
  components: {
    BanksMonthlyTransaction,
    CollectionsMonthlyTransaction,
    FundsMonthlyTransaction,
    ODMonthlyTransaction,
  },
  data: () => ({
    activeSection: '',
  }),
  props: {
    baseCrudKey: {
      type: String,
      default: 'SaiMonthlyTrans-dialog',
    },
  },
  created() {
  },
  computed: {
    ...mapGetters('base/crud', [
      'item',
      'items',
      'index',
      'isVisible',
    ]),
    ...mapState({
      SaiMonthlyTransactionSectionTypes: (state) => state.enums.SaiMonthlyTransactionSectionEnum,
    }),
    ...mapState('OD', [
      'departments',
    ]),
    editedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    saiSections() {
      if (!this.SaiMonthlyTransactionSectionTypes) {
        return [];
      }
      return this.SaiMonthlyTransactionSectionTypes;
    },
    isVisibleProp() {
      const isVisible = this.isVisible(this.baseCrudKey);
      if (isVisible) {
        this.sectionVisible();
      }
      return [];
    },
    banks() {
      const bankObjs = this.items(bankCrudKey) || [];
      return [{ _id: null, bankName: '' }, ...bankObjs];
    },
    funds() {
      const fundList = this.items(fundCrudKey) || [];
      const updatedFundObj = fundList.map((f) => ({
        ...f,
        fundDesc: f.fund === '0000' ? f.description : `${f.fund} - ${f.description || ''}`,
      }));
      return [{ _id: null, fund: '', fundDesc: '' }, ...updatedFundObj];
    },
    departmentList() {
      return this.departments === undefined ? undefined : [{
        id: '',
        dept: 0,
        description: '',
        caption: '',
      }, ...this.departments];
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setItem',
    ]),
    sectionVisible() {
      if (this.editedItem.section === 'BANKS') {
        this.activeSection = 'BANKS';
      }
      if (this.editedItem.section === 'COLLECTIONS') {
        this.activeSection = 'COLLECTIONS';
      }
      if (this.editedItem.section === 'FUNDS') {
        this.activeSection = 'FUNDS';
      }
      if (this.editedItem.section === 'OFFICIALDEPOSITORY') {
        this.activeSection = 'OFFICIALDEPOSITORY';
      }
      return this.activeSection;
    },
    updateBanksMonthlyTransaction(value) {
      const newEditedItemValue = {
        ...this.editedItem,
        banksMonthlyTransaction: value,
      };
      this.setItem([this.baseCrudKey, newEditedItemValue]);
    },
    updateCollectionsMonthlyTransaction(value) {
      const newEditedItemValue = {
        ...this.editedItem,
        collectionMonthlyTransaction: value,
      };
      this.setItem([this.baseCrudKey, newEditedItemValue]);
    },
    roundBankAmountFields() {
      return bankSection.forEach((f) => {
        this.editedItem.banksMonthlyTransaction[f] = amountFormat(
          amountToNumber(this.editedItem.banksMonthlyTransaction[f]),
        );
      });
    },
    roundFundCollectionsAmountFields() {
      if (this.editedItem.section === 'COLLECTIONS') {
        return collectionSection.forEach((f) => {
          this.editedItem.collectionMonthlyTransaction[f] = amountFormat(
            amountToNumber(this.editedItem.collectionMonthlyTransaction[f]),
          );
        });
      }
      if (this.editedItem.section === 'FUNDS') {
        return fundSection.forEach((f) => {
          this.editedItem.fundsMonthlyTransaction[f] = amountFormat(
            amountToNumber(this.editedItem.fundsMonthlyTransaction[f]),
          );
        });
      }
      return this.editedItem;
    },
    roundODAmountFields() {
      return odSection.forEach((f) => {
        this.editedItem.odMonthlyTransaction[f] = amountFormat(
          amountToNumber(this.editedItem.odMonthlyTransaction[f]),
        );
      });
    },
  },
};
</script>
